import { z } from "zod";
import { getIdSchema, ID, makeIdGenerator } from "../utils";

export const oauthAccessTokenIdPrefix = "oat_";
export type OAuthAccessTokenId = ID<typeof oauthAccessTokenIdPrefix>;
export const generateOAuthAccessTokenId = makeIdGenerator(
  oauthAccessTokenIdPrefix,
);
export const oauthAccessTokenIdSchema = getIdSchema(
  oauthAccessTokenIdPrefix,
  "OAuthAccessTokenId",
);

export const oauthClientSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    publisher: z.string(),
    verified: z.boolean(),
    logoUrl: z.string(),
    // Sentence shown beneath the consent prompt, e.g. "Send harvest weights
    // straight into Fat Bees — no spreadsheets, no re-typing." Each client
    // owns its own pitch; the consent UI is generic.
    description: z.string(),
  })
  .meta({ id: "OAuthClient" });

export type OAuthClient = z.infer<typeof oauthClientSchema>;

export const oauthAuthorizationInfoSchema = z
  .object({
    client: oauthClientSchema,
    user: z.object({
      id: z.string(),
      name: z.string(),
      email: z.string(),
    }),
  })
  .meta({ id: "OAuthAuthorizationInfo" });

export type OAuthAuthorizationInfo = z.infer<
  typeof oauthAuthorizationInfoSchema
>;

export const oauthAuthorizeRequestSchema = z
  .object({
    client_id: z.string(),
    redirect_uri: z.string(),
    code_challenge: z.string(),
    code_challenge_method: z.literal("S256"),
    state: z.string().optional(),
    response_type: z.literal("code"),
    decision: z.enum(["allow", "deny"]),
  })
  .meta({ id: "OAuthAuthorizeRequest" });

export type OAuthAuthorizeRequest = z.infer<typeof oauthAuthorizeRequestSchema>;

export const oauthAuthorizeResponseSchema = z
  .object({
    redirectTo: z.string(),
  })
  .meta({ id: "OAuthAuthorizeResponse" });

export type OAuthAuthorizeResponse = z.infer<
  typeof oauthAuthorizeResponseSchema
>;

export const oauthConnectedAppSchema = z
  .object({
    id: oauthAccessTokenIdSchema,
    client: oauthClientSchema,
    connectedAt: z.coerce.date(),
    lastUsedAt: z.coerce.date().nullable(),
    tokenPreview: z.string(),
  })
  .meta({ id: "OAuthConnectedApp" });

export type OAuthConnectedApp = z.infer<typeof oauthConnectedAppSchema>;
